import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Issue #109 (epic #107): on a phone, `w-full` on a `fixed`-positioned
// element resolves against the viewport, so the old default (`w-full
// max-w-lg`, both unprefixed) rendered genuinely edge-to-edge below
// whatever max-w a call site chose — none of `max-w-lg`/`2xl`/`3xl` ever
// engage below ~672-768px, i.e. never on a phone. Splitting WIDTH and
// MAX-WIDTH into separate Tailwind class groups fixes that without
// touching call sites' own `max-w-*` overrides:
//   - `w-[calc(100%-2rem)]` (unprefixed) is an explicit width, smaller than
//     any max-w-* a call site sets, so it — not the cap — decides the
//     rendered width below `sm:`. That's the "gutter" (1rem/16px per side).
//   - `sm:w-full` (a *different* modifier group in tailwind-merge, so it
//     survives alongside a call site's own bare `max-w-*`) hands control
//     back to max-width at `sm:` and up, reproducing the exact desktop
//     sizing every call site already had (requirement: "desktop unchanged").
//
// Height is capped and turned into "body scrolls, header/footer pinned" by
// DialogContent itself below, rather than by call sites individually
// stacking `max-h-[NNvh] overflow-y-auto` on the whole content box: that
// old pattern scrolled the header away with the body, and — per the #113
// harness's `dialog content wider than dialog` assertion — setting
// `overflow-y` makes the computed `overflow-x` become `auto` too, so a form
// too wide for the dialog reads as "scrollable" instead of "overflowing".
// The fix here is structural (a flex column with a single scrolling
// middle), not another overflow utility for content to hide sideways in.
const DIALOG_CONTENT_CLASS =
  "fixed left-[50%] top-[50%] z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-4 overflow-hidden border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:w-full sm:max-h-[85vh] sm:rounded-lg";

const DialogContent = React.forwardRef(({ className, bodyClassName, children, ...props }, ref) => {
  // `bodyClassName` (#1334) is opt-in extra classes for the scrolling body only; no caller
  // passes it unless it needs them, so every other dialog renders the same markup as before.
  // Auto-split direct children into header / footer / everything-else so
  // every existing call site gets "header and footer stay put, the middle
  // scrolls" for free — no per-file JSX restructuring needed. Matched by
  // component identity (not displayName strings) so it's robust to
  // wrapping; anything that isn't a DialogHeader/DialogFooter (including a
  // bare DialogTitle some call sites render standalone) falls into the
  // scrollable body, which is always safe since a header/footer is never
  // required.
  const kids = React.Children.toArray(children);
  const header = kids.filter((c) => c?.type === DialogHeader);
  const footer = kids.filter((c) => c?.type === DialogFooter);
  const body = kids.filter((c) => c?.type !== DialogHeader && c?.type !== DialogFooter);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(DIALOG_CONTENT_CLASS, className)}
        {...props}>
        {header.length > 0 && <div className="shrink-0">{header}</div>}
        {/* overflow-x-hidden (not just overflow-y-auto) is deliberate: per
            spec, an element with overflow-y:auto and overflow-x left
            unspecified computes overflow-x to auto too, which would make
            genuinely-too-wide body content quietly scrollable sideways
            instead of visibly broken — exactly the failure mode the #113
            harness's "dialog content wider than dialog" check exists to
            catch. Pinning overflow-x:hidden here means a form that doesn't
            collapse to the viewport gets clipped (loudly wrong, caught by
            the harness) rather than smuggled out via a second axis. */}
        <div className={cn("min-h-0 flex-1 overflow-y-auto overflow-x-hidden", bodyClassName)}>{body}</div>
        {footer.length > 0 && <div className="shrink-0">{footer}</div>}
        <DialogPrimitive.Close
          className="absolute right-0.5 top-0.5 inline-flex h-11 w-11 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
